<template>
  <div class="vessel-blacklist-panel" :class="{'panel-collapsed': isCollapsed}">
    <div class="panel-header">
      <h3 class="panel-title">
        <i class="el-icon-warning-outline mr-2"></i>
        船舶黑白名单
      </h3>
      <div class="panel-controls">
        <i class="el-icon-close" @click="closePanel" title="关闭"></i>
      </div>
    </div>
    
    <div class="panel-body" v-show="!isCollapsed">
      <div class="panel-toolbar">
        <el-radio-group v-model="listType" size="small" @change="handleListTypeChange">
          <el-radio-button :label="1">黑名单</el-radio-button>
          <el-radio-button :label="0">白名单</el-radio-button>
        </el-radio-group>
        
        <div class="flex-spacer"></div>
        
        <el-input
          placeholder="搜索MMSI或船名"
          v-model="searchQuery"
          class="search-input"
          size="small"
          prefix-icon="el-icon-search"
          @input="handleSearch"
          clearable>
        </el-input>
        
        <el-button 
          type="primary" 
          size="small" 
          icon="el-icon-plus"
          @click="handleAddClick">
          添加
        </el-button>
        
        <!-- 新增警戒规则按钮，仅在黑名单模式下显示 -->
        <el-button 
          v-if="listType === 1"
          type="success" 
          size="small" 
          icon="el-icon-bell"
          @click="showAlertRules">
          警戒规则
        </el-button>
        
        <!-- 新建白名单规则按钮，仅在白名单模式下显示 -->
        <el-button 
          v-if="listType === 0"
          type="success" 
          size="small" 
          icon="el-icon-plus"
          @click="handleAddWhitelistRule">
          新建规则
        </el-button>
        
        <!-- 白名单说明，仅在白名单模式下显示 -->
        <el-tooltip 
          v-if="listType === 0"
          content="白名单船舶不会触发告警，但仍会正常显示和记录。支持按船舶类型、MMSI模式、船舶名称创建规则。"
          placement="top">
          <i class="el-icon-info" style="margin-left: 8px; color: #909399; cursor: help;"></i>
        </el-tooltip>
      </div>
      
      <el-table
        :data="displayList"
        style="width: 100%"
        size="small"
        v-loading="loading"
        :max-height="400"
        border>
        <el-table-column prop="mmsi" label="MMSI" width="120"></el-table-column>
        <el-table-column prop="vesselName" label="船舶名称" width="150">
          <template slot-scope="scope">
            <div>
              {{ scope.row.cnName || scope.row.vesselName || '-' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="vesselTypeName" label="船舶类型" width="120">
          <template slot-scope="scope">
            {{ scope.row.vesselTypeName || scope.row.vesselType || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="reason" label="加入原因" show-overflow-tooltip></el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="160">
          <template slot-scope="scope">
            {{ $options.filters.dateFormat(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" width="100">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="small" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="panel-pagination">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next"
          :total="total">
        </el-pagination>
      </div>
      
      <!-- 白名单规则管理区域，仅在白名单模式且显示规则时显示 -->
      <div v-if="listType === 0 && whitelistRulesVisible" class="whitelist-rules-section">
        <div class="rules-section-header">
          <h4 class="rules-section-title">
            <i class="el-icon-shield mr-2"></i>
            白名单规则配置
          </h4>
          <el-button 
            type="text" 
            size="small" 
            icon="el-icon-close"
            @click="handleWhitelistRulesClose">
            收起
          </el-button>
        </div>
        
        <div class="rules-section-content">
          <vessel-whitelist-rule-panel
            ref="whitelistRulePanel"
            :bridge-id="bridgeId"
            :is-embedded="true"
            @close="handleWhitelistRulesClose"
          />
        </div>
      </div>
    </div>
    
    <!-- 添加/编辑黑白名单抽屉面板 -->
    <el-drawer
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      direction="rtl"
      size="500px"
      :show-close="true"
      :wrapper-closable="false"
      append-to-body>
      <div class="drawer-content">
        <el-form 
          :model="form" 
          :rules="rules" 
          ref="vesselForm" 
          label-width="100px"
          size="small"
          class="drawer-form">
          <!-- 搜索输入框 -->
          <el-form-item label="查询" prop="searchInput">
            <el-input 
              v-model="form.mmsi" 
              placeholder="搜索MMSI或船名"
              @blur="searchVesselInfo">
            </el-input>
          </el-form-item>
          
          <!-- MMSI显示 -->
          <el-form-item label="MMSI" prop="mmsi">
            <el-input 
              v-model="form.mmsi" 
              placeholder="MMSI"
              :disabled="true"
              class="readonly-field">
            </el-input>
          </el-form-item>
          
          <!-- 英文名称 -->
          <el-form-item label="英文名称" prop="vesselName">
            <el-input 
              v-model="form.vesselName" 
              placeholder="船舶英文名称"
              :disabled="true"
              class="readonly-field">
            </el-input>
          </el-form-item>
          
          <!-- 中文名称 -->
          <el-form-item label="中文名称" prop="cnName">
            <el-input 
              v-model="form.cnName" 
              placeholder="船舶中文名称"
              :disabled="true"
              class="readonly-field">
            </el-input>
          </el-form-item>
          
          
          <!-- 船舶类型 -->
          <el-form-item label="船舶类型" prop="vesselTypeName">
            <el-input 
              v-model="form.vesselTypeName" 
              placeholder="船舶类型"
              :disabled="true"
              class="readonly-field">
            </el-input>
          </el-form-item>
          
          <!-- 名单类型 -->
          <el-form-item label="名单类型" prop="listType">
            <el-radio-group v-model="form.listType">
              <el-radio :label="1">黑名单</el-radio>
              <el-radio :label="0">白名单</el-radio>
            </el-radio-group>
          </el-form-item>
          
          <!-- 加入原因 -->
          <el-form-item label="加入原因" prop="reason">
            <el-input 
              type="textarea" 
              v-model="form.reason" 
              rows="3"
              placeholder="请输入加入该名单的原因（可选）">
            </el-input>
          </el-form-item>
        </el-form>
        
        <div class="drawer-footer">
          <el-button @click="dialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
        </div>
      </div>
    </el-drawer>
    
    <!-- 黑名单警戒规则组件 -->
    <bridge-alert-rule-drawer
      v-model="alertRulesVisible"
      :rule="currentRule"
      :bridge-id="bridgeId"
      :area-id="null"
      :area-name="'黑名单区域'"
      :rule-type="'BLACKLIST'"
      :alert-types="alertTypes"
      :alert-levels="alertLevels"
      :alert-methods="alertMethods"
      :area-type="'BLACKLIST'"
      @success="handleRuleSaved"
      @close="handleRuleClose"
    />
    
  </div>
</template>

<script>
import {
  getBlacklistList,
  getVesselBlacklistByBridgeId,
  getBlacklistById,
  createBlacklist,
  updateBlacklist,
  deleteBlacklist
} from '@/api/vessel-blacklist';
import { getVesselByMmsi, getAllVesselTypes } from '@/api/vessel';
import * as alertApi from '@/api/alert';
import BridgeAlertRuleDrawer from './BridgeAlertRuleDrawer.vue';
import VesselWhitelistRulePanel from './VesselWhitelistRulePanel.vue';
import { formatDate } from '@/utils/dateUtils';

export default {
  name: 'VesselBlacklistPanel',
  
  components: {
    BridgeAlertRuleDrawer,
    VesselWhitelistRulePanel
  },
  
  filters: {
    dateFormat(value) {
      return formatDate(value);
    }
  },
  
  props: {
    bridgeId: {
      type: [String, Number],
      required: true
    }
  },
  
  data() {
    return {
      isCollapsed: false,
      loading: false,
      submitting: false,
      loadingVesselInfo: false,
      listType: 1, // 默认显示黑名单 (1-黑名单，0-白名单)
      searchQuery: '',
      isSearchByName: false, // 是否按船名查询船舶信息
      vesselTypeOptions: [], // 船舶类型选项
      vesselTypeMap: {}, // 船舶类型代码到名称的映射
      
      // 表单数据
      form: {
        id: null,
        bridgeId: null,
        mmsi: '',
        vesselName: '',
        cnName: '', // 中文名
        vesselType: '',
        vesselTypeName: '', // 船舶类型中文名称
        listType: 1,
        reason: '',
        createTime: null,
        updateTime: null
      },
      
      // 表单校验规则
      rules: {
        mmsi: [
          { required: true, message: '请输入船舶MMSI', trigger: 'blur' },
          { max: 20, message: 'MMSI长度不能超过20个字符', trigger: 'blur' }
        ],
        vesselName: [
          { required: false, message: '请输入船舶名称', trigger: 'blur' }
        ],
        listType: [
          { required: true, message: '请选择名单类型', trigger: 'change' }
        ],
        reason: [
          { required: false, message: '请输入加入原因（可选）', trigger: 'blur' },
          { max: 500, message: '原因长度不能超过500个字符', trigger: 'blur' }
        ]
      },
      
      // 对话框控制
      dialogVisible: false,
      dialogTitle: '添加船舶名单',
      isEdit: false,
      
      // 警戒规则相关
      alertRulesVisible: false,
      currentRule: null,
      alertTypes: [],
      alertLevels: [],
      alertMethods: [],
      ruleTypes: [], // 新增的ruleTypes属性
      
      // 白名单规则相关
      whitelistRulesVisible: false,
      
      // 列表数据
      blacklist: [],
      whitelist: [],
      currentPage: 1,
      pageSize: 10,
      total: 0
    };
  },
  
  computed: {
    // 根据当前显示的名单类型和搜索词过滤显示的列表
    displayList() {
      const list = this.listType === 1 ? this.blacklist : this.whitelist;
      
      if (!this.searchQuery) {
        return list;
      }
      
      const query = this.searchQuery.toLowerCase();
      return list.filter(item => {
        return (
          (item.mmsi && item.mmsi.toLowerCase().includes(query)) ||
          (item.vesselName && item.vesselName.toLowerCase().includes(query))
        );
      });
    }
  },
  
  watch: {
    bridgeId: {
      handler(newVal) {
        if (newVal) {
          this.loadVesselList();
        }
      },
      immediate: true
    }
  },
  
  created() {
    // 加载船舶类型数据
    this.loadVesselTypes();
    
    // 加载告警配置选项
    this.fetchAlertOptions();
  },
  
  methods: {
    // 显示警戒规则面板
    async showAlertRules() {
      try {
        // 查询是否已经存在黑名单规则
        const params = {
          bridgeId: this.bridgeId,
          ruleType: 'BLACKLIST',
          pageNum: 1,
          pageSize: 10
        };
        
        const response = await alertApi.getAlertRules(params);
        
        if (response && response.code === 200) {
          const rules = response.data.list || [];
          // 如果已经存在规则，编辑第一条
          if (rules.length > 0) {
            this.currentRule = rules[0];
          } else {
            // 否则创建新规则
            this.currentRule = {
              ruleName: '黑名单船舶警戒规则',
              ruleType: 'BLACKLIST',
              detectionMethod: 'BLACKLIST',
              alertLevel: 'WARNING',
              // alertMethods: ['PAGE','RECORD'],
              alertMessage: '{桥梁名}：发现黑名单船舶：{船舶名}，MMSI：{mmsi}，请立即关注！',
              priority: 1,
              status: 1,
              bridgeId: this.bridgeId
            };
          }
          
          this.alertRulesVisible = true;
        } else {
          this.$message.error(response.message || '获取警戒规则失败');
        }
      } catch (error) {
        console.error('获取黑名单警戒规则失败:', error);
        this.$message.error('获取警戒规则失败');
        // 创建新规则
        this.currentRule = {
          ruleName: '黑名单船舶警戒规则',
          ruleType: 'BLACKLIST',
          detectionMethod: 'BLACKLIST',
          alertLevel: 'WARNING',
          // alertMethods: ['PAGE','RECORD'],
          alertMessage: '{桥梁名}：发现黑名单船舶：{船舶名}，MMSI：{mmsi}，请立即关注！',
          priority: 1,
          status: 1,
          bridgeId: this.bridgeId
        };
        this.alertRulesVisible = true;
      }
    },
    
    // 获取告警配置选项
    async fetchAlertOptions() {
      try {
        // 获取告警级别选项
        const levelRes = await alertApi.getAlertLevelOptions();
        if (levelRes && levelRes.code === 200) {
          this.alertLevels = levelRes.data || [];
        }
        
        // 获取告警方式选项
        const methodRes = await alertApi.getAlertMethodOptions();
        if (methodRes && methodRes.code === 200) {
          this.alertMethods = methodRes.data || [];
        }
        
        // 获取告警类型选项
        const typeRes = await alertApi.getRuleTypeOptions();
        if (typeRes && typeRes.code === 200) {
          // 添加黑名单类型选项
          const types = typeRes.data || [];
          const hasBlacklistType = types.some(type => type.value === 'BLACKLIST');
          
          if (!hasBlacklistType) {
            types.push({ value: 'BLACKLIST', label: '黑名单告警' });
          }
          
          this.alertTypes = types;
        }

        // 获取告警规则类型选项
        const ruleTypeRes = await alertApi.getRuleTypeOptions();
        if (ruleTypeRes && ruleTypeRes.code === 200) {
          this.ruleTypes = ruleTypeRes.data || [];
        }
      } catch (error) {
        console.error('获取告警配置选项失败:', error);
      }
    },
    
    // 处理规则保存成功
    handleRuleSaved(rule) {
      this.$message.success('黑名单警戒规则设置成功');
      this.alertRulesVisible = false;
    },
    
    // 处理规则面板关闭
    handleRuleClose() {
      this.alertRulesVisible = false;
    },
    
    // 显示白名单规则管理面板
    showWhitelistRules() {
      this.whitelistRulesVisible = true;
    },
    
    // 处理白名单规则面板关闭
    handleWhitelistRulesClose() {
      this.whitelistRulesVisible = false;
    },
    
    // 处理新建白名单规则
    handleAddWhitelistRule() {
      // 确保规则配置区域展开
      this.whitelistRulesVisible = true;
      
      // 通过ref调用子组件的新建方法
      this.$nextTick(() => {
        if (this.$refs.whitelistRulePanel) {
          this.$refs.whitelistRulePanel.handleAddRule();
        }
      });
    },
    
    // 使用公共方法格式化日期
    
    // 加载船舶名单列表
    async loadVesselList() {
      if (!this.bridgeId) return;
      
      this.loading = true;
      try {
        // 加载当前名单类型的数据
        const response = await getVesselBlacklistByBridgeId(this.bridgeId, this.listType);
        
                  // 服务端统一返回格式: {code: 200, message: "操作成功", data: [...]}
          if (response && response.code === 200) {
            const listData = response.data || [];
            
            if (this.listType === 1) {
              this.blacklist = listData;
              this.total = this.blacklist.length;
            } else {
              this.whitelist = listData;
              this.total = this.whitelist.length;
            }
            console.log(`成功加载${this.listType === 1 ? '黑' : '白'}名单数据，共${this.total}条记录`);
          } else {
            const errorMsg = response?.message || '获取船舶名单失败';
            console.warn('API响应异常:', response);
            this.$message.error(errorMsg);
          }
      } catch (error) {
        console.error('加载船舶名单失败:', error);
        this.$message.error('加载船舶名单失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 处理名单类型切换
    handleListTypeChange() {
      this.loadVesselList();
      
      // 切换到白名单时默认展开规则配置
      if (this.listType === 0) {
        this.whitelistRulesVisible = true;
      } else {
        this.whitelistRulesVisible = false;
      }
    },
    
    // 处理搜索
    handleSearch() {
      // 已通过计算属性实现实时搜索
    },
    
    // 关闭面板
    closePanel() {
      this.$emit('close');
    },
    
    // 分页大小变化
    handleSizeChange(size) {
      this.pageSize = size;
      this.loadVesselList();
    },
    
    // 当前页变化
    handleCurrentChange(page) {
      this.currentPage = page;
      this.loadVesselList();
    },
    
    // 添加船舶到名单
    handleAddClick() {
      this.isEdit = false;
      this.dialogTitle = '添加船舶名单';
      this.resetForm();
      this.form.listType = this.listType;
      this.form.bridgeId = this.bridgeId;
      this.dialogVisible = true;
    },
    
    // 编辑船舶名单
    async handleEdit(row) {
      this.isEdit = true;
      this.dialogTitle = '编辑船舶名单';
      this.resetForm();
      
      try {
        const response = await getBlacklistById(row.id);
        
        // 服务端统一返回格式: {code: 200, message: "操作成功", data: {...}}
        if (response && response.code === 200) {
          const detailData = response.data;
          if (detailData) {
            this.form = { ...detailData };
            console.log('成功获取船舶名单详情:', this.form.mmsi);
          } else {
            this.$message.warning('未找到船舶名单详情');
          }
        } else {
          const errorMsg = response?.message || '获取名单详情失败';
          console.warn('API响应异常:', response);
          this.$message.error(errorMsg);
        }
      } catch (error) {
        console.error('获取名单详情失败:', error);
        this.$message.error('获取名单详情失败');
      }
      
      this.dialogVisible = true;
    },
    
    // 查询船舶信息
    async searchVesselInfo() {
      if (!this.form.mmsi || this.form.mmsi.trim().length === 0) {
        return;
      }
      
      this.loadingVesselInfo = true;
      try {
        const mmsi = this.form.mmsi.trim();
        const response = await getVesselByMmsi(mmsi);
        
        // 服务端统一返回格式: {code: 200, message: "操作成功", data: {...}}
        if (response && response.code === 200) {
          const vesselData = response.data;
          if (vesselData) {
            // 填充船舶信息
            this.form.vesselName = vesselData.vesselName || '';
            this.form.cnName = vesselData.cnName || '';
            this.form.vesselType = vesselData.vesselType || '';
            this.form.vesselTypeName = this.getVesselTypeName(vesselData.vesselType) || vesselData.vesselTypeName || '';
            console.log('成功获取船舶信息:', this.form.mmsi);
          } else {
            this.$message.warning(`未找到MMSI为 ${mmsi} 的船舶信息`);
          }
        } else {
          const errorMsg = response?.message || '获取船舶信息失败';
          console.warn('API响应异常:', response);
          this.$message.error(errorMsg);
        }
      } catch (error) {
        console.error('查询船舶信息失败:', error);
        this.$message.error('查询船舶信息失败');
      } finally {
        this.loadingVesselInfo = false;
      }
    },
    
    // 获取船舶类型名称
    getVesselTypeName(typeCode) {
      return this.vesselTypeMap[typeCode] || typeCode;
    },
    
    // 加载船舶类型选项
    async loadVesselTypes() {
      try {
        const response = await getAllVesselTypes();
        
        // 服务端统一返回格式: {code: 200, message: "操作成功", data: [...]}
        if (response && response.code === 200) {
          this.vesselTypeOptions = response.data || [];
          
          // 构建类型代码到名称的映射
          this.vesselTypeMap = {};
          this.vesselTypeOptions.forEach(item => {
            this.vesselTypeMap[item.typeCode] = item.typeName;
          });
          
          console.log('成功加载船舶类型选项，共', this.vesselTypeOptions.length, '条');
        } else {
          console.warn('获取船舶类型选项响应异常:', response);
        }
      } catch (error) {
        console.error('加载船舶类型选项失败:', error);
      }
    },
    
    // 处理删除操作
    handleDelete(row) {
      this.$confirm('确认要从名单中删除该船舶吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await deleteBlacklist(row.id);
          
          // 服务端统一返回格式: {code: 200, message: "操作成功", data: null}
          if (response && response.code === 200) {
            this.$message.success(response.message || '删除成功');
            this.loadVesselList();
          } else {
            const errorMsg = response?.message || '删除失败';
            console.warn('API响应异常:', response);
            this.$message.error(errorMsg);
          }
        } catch (error) {
          console.error('删除名单失败:', error);
          this.$message.error('删除名单失败');
        }
      }).catch(() => {
        // 用户取消删除操作
      });
    },
    
    // 重置表单
    resetForm() {
      if (this.$refs.vesselForm) {
        this.$refs.vesselForm.resetFields();
      }
      
      // 非编辑模式时，重置查询方式
      if (!this.isEdit) {
        this.isSearchByName = false;
      }
      
      this.form = {
        id: null,
        bridgeId: this.bridgeId,
        mmsi: '',
        vesselName: '',
        cnName: '',
        vesselType: '',
        vesselTypeName: '',
        listType: this.listType,
        reason: '',
        createTime: null,
        updateTime: null
      };
    },
    
    // 提交表单
    submitForm() {
      this.$refs.vesselForm.validate(async (valid) => {
        if (!valid) return;
        
        this.submitting = true;
        try {
          let response;
          const actionType = this.isEdit ? '更新' : '添加';
          
          // 准备提交数据
          const submitData = {
            id: this.form.id,
            bridgeId: this.form.bridgeId,
            mmsi: this.form.mmsi,
            vesselName: this.form.vesselName,
            cnName: this.form.cnName,
            vesselType: this.form.vesselType,
            vesselTypeName: this.form.vesselTypeName,
            listType: this.form.listType,
            reason: this.form.reason,
            createTime: this.form.createTime,
            updateTime: this.form.updateTime
          };
          
          if (this.isEdit) {
            // 编辑模式
            response = await updateBlacklist(this.form.id, submitData);
          } else {
            // 添加模式
            response = await createBlacklist(submitData);
          }
          
          // 服务端统一返回格式: {code: 200, message: "操作成功", data: {...}}
          if (response && response.code === 200) {
            this.$message.success(response.message || `${actionType}成功`);
            this.dialogVisible = false;
            this.loadVesselList();
          } else {
            const errorMsg = response?.message || `${actionType}失败`;
            console.warn(`${actionType}名单API响应异常:`, response);
            this.$message.error(errorMsg);
          }
        } catch (error) {
          const actionType = this.isEdit ? '更新' : '添加';
          console.error(`${actionType}名单失败:`, error);
          this.$message.error(`${actionType}失败`);
        } finally {
          this.submitting = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.vessel-blacklist-panel {
  width: 800px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px;
  border-bottom: 1px solid #ebeef5;
  cursor: pointer;
  user-select: none;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.panel-controls {
  display: flex;
  align-items: center;
}

.panel-body {
  padding: 15px;
  max-height: 600px;
  overflow-y: auto;
}

.panel-toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.flex-spacer {
  flex-grow: 1;
}

.search-input {
  width: 200px;
  margin-right: 10px;
}

.panel-pagination {
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
}

.mr-2 {
  margin-right: 8px;
}

/* 面板折叠状态 */
.panel-collapsed {
  border-bottom: 1px solid #ebeef5;
}

/* 抽屉面板样式 */
.drawer-content {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.drawer-form {
  flex: 1;
  overflow-y: auto;
}

.drawer-footer {
  margin-top: 20px;
  text-align: right;
  padding-top: 10px;
  border-top: 1px solid #ebeef5;
}

/* 只读字段样式 */
.readonly-field input {
  background-color: #f5f7fa;
  color: #606266;
}

/* 船舶中文名称样式 */
.vessel-cn-name {
  font-size: 12px;
  color: #909399;
  margin-top: 3px;
}

/* 警戒规则按钮样式 */
.el-button.el-button--success {
  margin-left: 10px;
}

/* 白名单规则管理区域样式 */
.whitelist-rules-section {
  margin-top: 20px;
  border-top: 2px solid #e4e7ed;
  padding-top: 15px;
}

.rules-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.rules-section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #67c23a;
  display: flex;
  align-items: center;
}

.rules-section-content {
  background-color: #fafafa;
  border-radius: 4px;
  padding: 15px;
}
</style> 